import { StepEnum } from '@/api-sdk';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles, typographyStyles } from '@/styles';
import { t } from 'i18next';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { iconCircleInfo } from '@/components/icons';

@customElement('fg-step-header')
export class FGStepHeader extends LitElement {
  @property({ type: String })
  stepType?:
    | StepEnum.quantity_per_variant
    | StepEnum.print_option_per_position
    | StepEnum.production_speed
    | undefined;

  @property({ type: Number })
  hspMaximumQuantity?: number | null;

  @state()
  stepTitle?: string;

  @state()
  stepIndex = 0;

  /**
   * Select the step title translation key based on the step type received from the configurator
   *
   * @param stepType {StepEnum.quantity_per_variant | StepEnum.print_option_per_position | StepEnum.production_speed | undefined}
   * @returns {string} Step title translation key
   */
  setStepTitleKey = (
    stepType:
      | StepEnum.quantity_per_variant
      | StepEnum.print_option_per_position
      | StepEnum.production_speed
      | undefined,
  ): void => {
    if (!stepType) {
      return;
    }
    const stepTitlesKeys = {
      [StepEnum.quantity_per_variant]: 'headers.selectItems',
      [StepEnum.print_option_per_position]: 'headers.selectPrintOptions',
      [StepEnum.production_speed]: 'headers.selectProductionSpeed',
    };

    /**
     * Set title index based on index of the step
     */
    this.stepIndex = Object.keys(stepTitlesKeys).indexOf(stepType);

    this.stepTitle = stepTitlesKeys[stepType];
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.stepType) {
      this.setStepTitleKey(this.stepType);
    }
  }

  render() {
    if (!this.stepTitle) {
      return;
    }
    const number = this.stepIndex + 1;

    return html`
      <span class="number">${number}</span>
      <h2 class="h4">${t(this.stepTitle)}</h2>

      ${this.hspMaximumQuantity
        ? html`
            <span class="hsp-text">
              ${iconCircleInfo}
              <span>
                ${t('labels.24hProductionMaximumPcs', {
                  count: this.hspMaximumQuantity,
                }) || ''}
              </span>
            </span>
          `
        : ''}
    `;
  }

  static styles = [
    baseStyles,
    typographyStyles,
    css`
      :host {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        width: 100%;
      }

      span.number {
        display: grid;
        place-items: center;
        width: 40px;
        height: 40px;
        aspect-ratio: 1 / 1;
        color: #fff;
        background-color: var(--fg-primary, var(--fg-fallback-primary));
        border-radius: var(--fg-borderradius--xs);
        font-weight: 600;
        font-size: 1rem;
      }

      h2 {
        display: flex;
        flex-wrap: wrap;
      }

      span.hsp-text {
        display: flex;
        gap: 8px;
        flex-grow: 1;
        font-size: 0.875rem;
        justify-content: end;
        width: auto;
      }

      span.hsp-text svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        height: 14px;
        flex-shrink: 0;
        margin-top: 0.375rem;
        width: 14px;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        :host {
          gap: 0.625rem;
        }

        span.number {
          font-size: 1.25rem;
          line-height: 1.25rem;
          height: 40px;
          width: 40px;
        }

        span.hsp-text {
          margin-left: 1rem;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        :host {
          flex-wrap: nowrap;
        }
        h2 {
          flex-wrap: nowrap;
          white-space: nowrap;
        }
      }
    `,
  ];
}
